/**
 * Status do caso alterado direto na etiqueta, e o interruptor Em andamento/Arquivado.
 *
 * Etiqueta: clicar abre a lista de status; escolher só prepara a mudança, que
 * aparece destacada com Confirmar e Cancelar ao lado. Só o Confirmar grava
 * (pedido do Paulo: ter certeza de que a alteração foi intencional).
 *
 * Interruptor "Arquivado": ligar arquiva, desligar volta para Em andamento.
 * Grava na hora e oferece "Desfazer" no aviso, que volta ao status anterior.
 *
 * Os dois ficam dentro de cartões que são links: os cliques não navegam.
 */
import { useState, type MouseEvent, type ReactNode } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { caseService } from '@/services/case-service'
import { Case, CaseStatus, CaseStatusLabels, SELECTABLE_CASE_STATUSES } from '@/types/case'
import { getErrorMessage } from '@/utils/error.utils'
import { cn } from '@/utils/cn'

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive'

export const CASE_STATUS_VARIANT: Record<CaseStatus, BadgeVariant> = {
  DRAFT: 'outline',
  ACTIVE: 'default',
  SUSPENDED: 'secondary',
  FINISHED: 'secondary',
  CLOSED_WON: 'secondary',
  CLOSED_LOST: 'destructive',
  CLOSED_SETTLED: 'secondary',
  ARCHIVED: 'outline',
  CONVERTED_TO_LEGAL: 'secondary',
}

const stop = (e: MouseEvent) => {
  e.preventDefault()
  e.stopPropagation()
}

/** Impede que o clique chegue ao link do cartão em volta. */
function Isolate({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={className} onClick={stop} onKeyDown={(e) => e.stopPropagation()} role="presentation">
      {children}
    </span>
  )
}

interface CaseStatusControlProps {
  caseId: string
  caseTitle: string
  status: CaseStatus
  /** Recebe o caso como a API devolveu depois da mudança. */
  onChanged: (updated: Case) => void
  className?: string
}

export function CaseStatusControl({ caseId, caseTitle, status, onChanged, className }: CaseStatusControlProps) {
  const [pending, setPending] = useState<CaseStatus | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const options = SELECTABLE_CASE_STATUSES.includes(status) ? SELECTABLE_CASE_STATUSES : [status, ...SELECTABLE_CASE_STATUSES]

  const confirm = async () => {
    if (!pending) return
    setSaving(true)
    setError(null)
    try {
      const updated = await caseService.changeCaseStatus(caseId, { status: pending })
      setPending(null)
      onChanged(updated)
      toast.success(`Status alterado para ${CaseStatusLabels[pending]}`)
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível alterar o status'))
    } finally {
      setSaving(false)
    }
  }

  const shown = pending ?? status

  return (
    <Isolate className={cn('inline-flex flex-wrap items-center gap-1.5', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={saving}>
          <button
            type="button"
            aria-label={`Status de ${caseTitle}: ${CaseStatusLabels[shown] ?? shown}. Clique para alterar`}
            className="rounded-4xl focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <Badge
              variant={pending ? 'outline' : CASE_STATUS_VARIANT[status] ?? 'secondary'}
              className={cn('cursor-pointer gap-0.5 pr-1.5', pending && 'border-dashed border-primary text-primary')}
            >
              {CaseStatusLabels[shown] ?? shown}
              <ChevronDown aria-hidden="true" />
            </Badge>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel>Alterar status</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup
            value={shown}
            onValueChange={(value) => {
              setError(null)
              setPending(value === status ? null : (value as CaseStatus))
            }}
          >
            {options.map((option) => (
              <DropdownMenuRadioItem key={option} value={option}>{CaseStatusLabels[option] ?? option}</DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {pending && (
        <>
          <Button size="xs" onClick={confirm} disabled={saving}>
            <Check aria-hidden="true" /> {saving ? 'Salvando…' : 'Confirmar'}
          </Button>
          <Button size="xs" variant="ghost" onClick={() => { setPending(null); setError(null) }} disabled={saving}>
            <X aria-hidden="true" /> Cancelar
          </Button>
        </>
      )}
      {error && <span role="alert" className="text-xs text-destructive">{error}</span>}
    </Isolate>
  )
}

interface CaseArchiveSwitchProps {
  caseId: string
  caseTitle: string
  status: CaseStatus
  onChanged: (updated: Case) => void
  className?: string
}

/** Interruptor "Arquivado": ligar arquiva, desligar volta para Em andamento. Sem entrar na edição do caso. */
export function CaseArchiveSwitch({ caseId, caseTitle, status, onChanged, className }: CaseArchiveSwitchProps) {
  const [saving, setSaving] = useState(false)
  const archived = status === CaseStatus.ARCHIVED

  const change = async (next: CaseStatus, previous: CaseStatus, offerUndo: boolean) => {
    setSaving(true)
    try {
      const updated = await caseService.changeCaseStatus(caseId, { status: next })
      onChanged(updated)
      if (offerUndo) {
        toast(next === CaseStatus.ARCHIVED ? 'Caso arquivado' : 'Caso de volta em andamento', {
          description: caseTitle,
          action: { label: 'Desfazer', onClick: () => { void change(previous, next, false) } },
        })
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Não foi possível alterar o caso'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Isolate className={cn('inline-flex items-center gap-2 text-xs text-muted-foreground', className)}>
      <span aria-hidden="true">Arquivado</span>
      <button
        type="button"
        role="switch"
        aria-checked={archived}
        aria-label={`${caseTitle} arquivado`}
        title={archived ? 'Clique para voltar a Em andamento' : 'Clique para arquivar'}
        disabled={saving}
        onClick={() => change(archived ? CaseStatus.ACTIVE : CaseStatus.ARCHIVED, status, true)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-wait disabled:opacity-60',
          archived ? 'bg-primary' : 'bg-input',
        )}
      >
        <span className={cn('pointer-events-none block size-4 rounded-full bg-background shadow transition-transform', archived ? 'translate-x-4' : 'translate-x-0.5')} />
      </button>
    </Isolate>
  )
}
